import React, { useState, useEffect } from "react";
import ServiceDetailsHeader from "../components/Common/ImageHeader";
import FeedbackSection from "../components/Common/Feedback";
import AIImpactSection from "../components/HomePage/Technology/AIImpactSection";
import AIBenefitsSection from "../components/HomePage/Technology/AIBenefitsSection";
import AIMLServicesSection from "../components/HomePage/Technology/AIMLServicesSection";
import { useNavigate, useParams } from "react-router-dom";
import { homePageTestimonialConfig } from "../components/Common/DifferentFeedbackConfig";
import {
  clientFeedbackService,
  formatClientFeedbackData,
} from "../services/clientFeedbackService";

const TechnologyDetails = () => {
  const navigate = useNavigate();
  const { slug, id } = useParams();

  // State for API data
  const [technologyData, setTechnologyData] = useState(null);
  const [specificTechData, setSpecificTechData] = useState(null);
  const [transformedData, setTransformedData] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Transform API data to component expected format
  const transformTechData = (rawData) => {
    if (!rawData) return null;

    
    const transformed = {
      // For AIImpactSection
      titleDesc: rawData.titleDesc,
      technologies: rawData.componentGrids || [],

      // For AIBenefitsSection
      keyBenefit: rawData.keyBenifit,
      benefits: rawData.benifitGrid || [],

      // For AIMLServicesSection
      serviceTitle: rawData.serviceTitileDesc,
      services: rawData.servicesGrids || [],
    };

    return transformed;
  };

  // Fetch technology data and testimonials on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // API base URL from environment variable with fallback
        const API_BASE_URL = process.env.REACT_APP_API_URL;

        // Fetch tech data with basic content first
        const basicResponse = await fetch(
          `${API_BASE_URL}/tech-details?populate=*`
        );
        const basicResult = await basicResponse.json();


        // Fetch tech data with images
        const imageResponse = await fetch(
          `${API_BASE_URL}/tech-details?populate[componentGrids][populate]=image&populate[benifitGrid][populate]=image&populate[servicesGrids][populate]=image`
        );
        const imageResult = await imageResponse.json();


        // Merge the data - basic content + images
        const techDataArray =
          basicResult.data?.map((techItem, index) => {
            const imageData = imageResult.data?.[index];
            return {
              ...techItem,
              componentGrids:
                imageData?.componentGrids || techItem.componentGrids,
              benifitGrid: imageData?.benifitGrid || techItem.benifitGrid,
              servicesGrids: imageData?.servicesGrids || techItem.servicesGrids,
            };
          }) || [];

        setTechnologyData(techDataArray);

        // Find specific technology based on ID or slug
        let specificItem = null;

        // If we have a slug that looks like a number (from ID navigation), treat it as ID
        const idToFind = id || (slug && !isNaN(slug) ? slug : null);

        if (idToFind) {
          specificItem = techDataArray.find(
            (tech) => tech.id === parseInt(idToFind)
          );
        } else if (slug && isNaN(slug)) {
          // Find by matching title from techHeader (only if slug is not a number)
          specificItem = techDataArray.find((tech) => {
            const titleSlug = tech.techHeader?.title
              ?.toLowerCase()
              .replace(/[^a-z0-9\s-]/g, "")
              .replace(/\s+/g, "-")
              .replace(/-+/g, "-")
              .trim("-");
            return titleSlug === slug;
          });
        }

        // Set the specific technology data or default to first item
        const selectedTech = specificItem || techDataArray[0];
        setSpecificTechData(selectedTech);

        // Transform data for components
        if (selectedTech) {
          const transformed = transformTechData(selectedTech);
          setTransformedData(transformed);
        }

        // Fetch client feedback data
        const feedbackResponse =
          await clientFeedbackService.getClientFeedback();
        if (feedbackResponse.success && feedbackResponse.data) {
          const formattedTestimonials = formatClientFeedbackData(
            feedbackResponse.data
          );
          setTestimonials(formattedTestimonials);
        }

        setError(null);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, id]);

  const handleBreadcrumbClick = (href) => {
    if (href && href !== "#") {
      navigate(href);
    }
  };

  // Show loading state
  if (loading) {
    return <div>Loading technology details...</div>;
  }

  // Show error state
  if (error) {
    return <div>Error loading technology details: {error}</div>;
  }
  return (
    <>
      <ServiceDetailsHeader
        title={specificTechData?.techHeader?.title || "Technology Details"}
        subtitle={
          specificTechData?.techHeader?.description ||
          "Explore our technology solutions"
        }
        backgroundImage="/png/technology.png"
        breadcrumbItems={[
          { label: "Home", href: "/" },
          { label: "Technology", href: "/#technology" },
          {
            label: specificTechData?.techHeader?.title || "Technology Details",
            href: "#",
            isCurrentPage: true,
          },
        ]}
        onBreadcrumbClick={handleBreadcrumbClick}
      />
      <div
        style={{
          backgroundImage: "url(/png/background.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <AIImpactSection data={transformedData} />
        <AIBenefitsSection data={transformedData} />
        <AIMLServicesSection data={transformedData} />
        <FeedbackSection
          testimonials={testimonials}
          config={homePageTestimonialConfig}
        />
      </div>
    </>
  );
};

export default TechnologyDetails;
